import { DateInput, DateRange, Popover, ButtonWithIcon } from '@clickcease/cc-client-components-lib'
import { fromUnixTime, getUnixTime } from 'date-fns'
import { debounce } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Filter } from '../../../../assets/icons/Filter'
import { Search } from '../../../../assets/icons/Search'
import { rangeOptions } from '../../../../components/DateRangeSelect/utilities'
import { InputWithIcon } from '../../../../components/Elements/InputWithIcon/InputWithIcon'
import { ActiveFilter } from '../ActiveFilter/ActiveFilter'
import { AdvancedFilters } from '../AdvancedFilters/AdvancedFilters'
import {
  ActiveFilterType,
  AdvancedFiltersState,
  categoryToFilter,
} from '../AdvancedFilters/AdvancedFilters.models'
import {
  FiltersRow,
  TagsAndFiltersContainer,
  DateAndAdvancedFiltersSection,
  TagsContainer,
} from '../FiltersBar/FiltersBar.styled'
import { useBMStore } from '../../../../stores/useBMStore'

interface FiltersBarProps {
  locale?: string
}

export const FiltersBar: React.FC<FiltersBarProps> = ({ locale }) => {
  const { t } = useTranslation('botMitigation')

  const [searchFilter, setSearchFilter] = useState('')
  const setSearchQuery = useBMStore((state) => state.setSearchQuery)
  const dateRange = useBMStore((state) => state.dateRange)
  const setDateRange = useBMStore((state) => state.setDateRange)
  const setAdvancedFilters = useBMStore((state) => state.setAdvancedFilters)
  const advancedFilters = useBMStore((state) => state.advancedFilters)

  const [localDateRange, setLocalDateRange] = useState<{ from: number; to: number }>(() => {
    if (dateRange && dateRange.fromDate && dateRange.toDate) {
      try {
        const fromDate = new Date(dateRange.fromDate)
        const toDate = new Date(dateRange.toDate)

        if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
          return {
            from: getUnixTime(fromDate),
            to: getUnixTime(toDate),
          }
        }
      } catch (e) {
        console.error('Error parsing dates from store:', e)
      }
    }

    return {
      from: getUnixTime((rangeOptions.WEEK.range as DateRange).from as Date),
      to: getUnixTime((rangeOptions.WEEK.range as DateRange).to as Date),
    }
  })

  useEffect(() => {
    if (!dateRange.fromDate || !dateRange.toDate) {
      const fromDate = fromUnixTime(localDateRange.from)
      const toDate = fromUnixTime(localDateRange.to)
      setDateRange(fromDate, toDate)
    }
  }, [dateRange.fromDate, dateRange.toDate, localDateRange.from, localDateRange.to, setDateRange])

  const debouncedFn = useRef(
    debounce((value: string) => {
      setSearchQuery(value)
    }, 1000)
  ).current

  useEffect(() => {
    return () => {
      debouncedFn.cancel()
    }
  }, [debouncedFn])

  const handleSearchChange = (value: string) => {
    setSearchFilter(value)
    debouncedFn(value)
  }

  const dateRangeForInput: DateRange = {
    from: fromUnixTime(localDateRange.from),
    to: fromUnixTime(localDateRange.to),
  }

  const onDateRangeSelected = (newDateRange: DateRange | undefined) => {
    if (newDateRange && newDateRange.from && newDateRange.to) {
      const newFromDate = newDateRange.from
      const newToDate = newDateRange.to

      const newLocalRange = {
        from: getUnixTime(newFromDate),
        to: getUnixTime(newToDate),
      }
      setLocalDateRange(newLocalRange)

      setDateRange(newFromDate, newToDate)
    }
  }

  const handleApplyFilters = (filters: AdvancedFiltersState) => {
    setAdvancedFilters(filters)
  }

  const removeFilter = (filterName: string) => {
    if (!advancedFilters) return

    const updatedFilters = { ...advancedFilters }
    if (filterName in updatedFilters) {
      updatedFilters[filterName] = []
      setAdvancedFilters(updatedFilters as AdvancedFiltersState)
    }
  }

  const getActiveFilters = (): ActiveFilterType[] => {
    if (!advancedFilters) return []
    return Object.entries(advancedFilters)
      .filter(([, values]) => Array.isArray(values) && values.length > 0)
      .map(([name, values]) => ({
        name,
        values: values as string[],
      }))
  }

  return (
    <TagsAndFiltersContainer>
      <FiltersRow>
        <InputWithIcon
          value={searchFilter}
          onChange={handleSearchChange}
          icon={<Search width={'14px'} height={'14px'} color={'#9B9B9B'} />}
          placeholder={t('filtersBar.search')}
        />
        <DateAndAdvancedFiltersSection>
          <DateInput
            localeFormat={locale}
            onSelect={onDateRangeSelected}
            selectedDateRange={dateRangeForInput}
          />
          <Popover
            style={{ marginRight: '16px' }}
            trigger={
              <div>
                <ButtonWithIcon
                  text={t('filtersBar.advanced_filters')}
                  iconLeft={<Filter width={'14px'} height={'14px'} />}
                  onClick={() => {}}
                />
              </div>
            }
            content={<AdvancedFilters onApplyFilters={handleApplyFilters} />}
          />
        </DateAndAdvancedFiltersSection>
      </FiltersRow>
      <TagsContainer>
        {getActiveFilters().map((currFilter: ActiveFilterType) => (
          <ActiveFilter
            key={`filterTag${currFilter.name}`}
            filterName={
              categoryToFilter.find((category) => category.value === currFilter.name)?.value ||
              currFilter.name
            }
            selectedOptions={currFilter.values || []}
            onRemove={() => {
              removeFilter(currFilter.name)
            }}
          />
        ))}
      </TagsContainer>
    </TagsAndFiltersContainer>
  )
}
